import { useEffect, useMemo, useState } from "react";
import { Mail, Send, Search, CheckCircle2, AlertCircle, History, TestTube2, X, Users, User as UserIcon, Radio, Beaker } from "lucide-react";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { ConfidentialityCatchupCard } from "@/components/admin/ConfidentialityCatchupCard";
import { useAuth } from "@/contexts/AuthContext";
import { EmailRecipientFilters, RecipientBadges, SendConfirmDialog, OutsideFilterWarning, audienceText, matchesFilters } from "@/components/admin/EmailRecipientFilters";

const SEND_TYPE_META = {
  // Bumped background opacity 0.10 → 0.28 and border 0.30 → 0.55 for the
  // Broadcast/Targeted badges so they no longer look near-transparent on
  // either cream or dark backgrounds. Targeted now uses `--sb-clay-2` for
  // text (was plain `--sb-clay` — mid-tone on light-clay bg = washed).
  broadcast: { label: "Broadcast", icon: Radio, tone: "bg-[hsl(var(--sb-forest))]/28 text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest))]/55" },
  targeted:  { label: "Targeted",  icon: Users, tone: "bg-[hsl(var(--sb-clay))]/28 text-[hsl(var(--sb-clay-2))] border-[hsl(var(--sb-clay))]/55" },
  individual:{ label: "Individual",icon: UserIcon, tone: "bg-muted text-foreground border-border" },
  test:      { label: "Test",      icon: Beaker, tone: "bg-amber-200 text-amber-950 border-amber-500 dark:bg-amber-900/50 dark:text-amber-100 dark:border-amber-600/70" },
};

function SendTypeChip({ sendType, className = "" }) {
  const meta = SEND_TYPE_META[sendType] || SEND_TYPE_META.broadcast;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.tone} ${className}`}
      data-testid={`admin-email-sendtype-${sendType}`}
    >
      <Icon size={11} /> {meta.label}
    </span>
  );
}

export default function AdminEmailCenter() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes("admin");
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Map());
  const [roles, setRoles] = useState([]);
  const [activity, setActivity] = useState("any");
  const [showSelected, setShowSelected] = useState(false);
  const [includeQa, setIncludeQa] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [lastSent, setLastSent] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [detail, setDetail] = useState(null);      // full send record
  const [detailLoading, setDetailLoading] = useState(false);
  const [consentEvents, setConsentEvents] = useState([]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/email-center/sends/${id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDetailLoading(false); }
  };

  const activityOn = roles.includes("author_only") || roles.includes("both");
  const recipientParams = { q: q || undefined, roles: roles.join(",") || undefined, activity: activityOn ? activity : "any", include_qa: includeQa || undefined };
  const isFiltered = roles.length > 0 || (activityOn && activity !== "any") || !!q.trim();

  const reload = async () => {
    try {
      const [s, r, h, c] = await Promise.allSettled([
        api.get("/admin/email-center/stats"),
        api.get("/admin/email-center/recipients", { params: recipientParams }),
        api.get("/admin/email-center/sends"),
        api.get("/admin/email-center/consent-events", { params: { limit: 100 } }),
      ]);
      if (s.status === "fulfilled") setStats(s.value.data);
      if (r.status === "fulfilled") setRecipients(r.value.data);
      if (h.status === "fulfilled") setHistory(h.value.data);
      if (c.status === "fulfilled") setConsentEvents(c.value.data);
      const failed = [s, r, h, c].find((x) => x.status === "rejected");
      if (failed) toast.error(formatApiError(failed.reason));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (isAdmin) reload();
    // eslint-disable-next-line
  }, [isAdmin]);

  useEffect(() => {
    let stale = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/email-center/recipients", { params: recipientParams });
        if (!stale) setRecipients(data);
      } catch { /* noop */ }
    }, 300);
    return () => { stale = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [q, roles, activity, includeQa]);

  const canSend = useMemo(() => subject.trim().length > 1 && body.trim().length > 1 && !sending, [subject, body, sending]);

  const targetedCount = selectedIds.size;
  const isTargeted = targetedCount > 0;
  const broadcastCount = (stats?.opted_in ?? 0) + (includeQa ? (stats?.qa_opted_in ?? 0) : 0);
  const outboundCount = isTargeted ? targetedCount : broadcastCount;

  const toggleOne = (r) => {
    setSelectedIds((prev) => {
      const next = new Map(prev);
      if (next.has(r.id)) next.delete(r.id); else next.set(r.id, r);
      return next;
    });
  };

  const allVisibleSelected = recipients.length > 0 && recipients.every((r) => selectedIds.has(r.id));
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Map(prev);
      if (allVisibleSelected) {
        recipients.forEach((r) => next.delete(r.id));
      } else {
        recipients.forEach((r) => next.set(r.id, r));
      }
      return next;
    });
  };

  const clearSelection = () => { setSelectedIds(new Map()); setShowSelected(false); };
  const appliedActivity = activityOn ? activity : "any";
  const outsideList = Array.from(selectedIds.values()).filter((r) => !matchesFilters(r, roles, appliedActivity));
  const outsideFilter = outsideList.length;
  const removeOutside = () => {
    setSelectedIds((prev) => {
      const next = new Map(prev);
      outsideList.forEach((r) => next.delete(r.id));
      return next;
    });
  };
  const toggleIncludeQa = (on) => {
    setIncludeQa(on);
    if (!on) {
      setSelectedIds((prev) => new Map(Array.from(prev).filter(([, r]) => !r.is_qa)));
    }
  };
  const listed = showSelected ? Array.from(selectedIds.values()) : recipients;
  const blockedBroadcast = !isTargeted && (isFiltered || !stats?.opted_in);
  const filterLabel = audienceText(roles, appliedActivity) + (q.trim() ? ` · search "${q.trim()}"` : "");
  const audienceLabel = !isTargeted
    ? `All opted-in members (broadcast)${includeQa ? " incl. QA accounts" : ""}`
    : outsideFilter > 0
      ? `Custom selection (${targetedCount} member${targetedCount === 1 ? "" : "s"}, ${outsideFilter} outside ${audienceText(roles, appliedActivity)})`
      : !q.trim() && selectedIds.size === recipients.length
        ? audienceText(roles, appliedActivity)
        : `${audienceText(roles, appliedActivity)} · ${targetedCount} selected`;
  const openConfirm = () => { if (canSend && !blockedBroadcast) setConfirming(true); };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const payload = { subject, body, audience_label: audienceLabel, include_qa: includeQa };
      if (isTargeted) payload.user_ids = Array.from(selectedIds.keys());
      const { data } = await api.post("/admin/email-center/send", payload);
      setLastSent(data);
      const delivered = data.delivered ?? data.recipient_count;
      toast.success(`Delivered to ${delivered} of ${data.recipient_count} recipient${data.recipient_count === 1 ? "" : "s"}.`);
      setSubject("");
      setBody("");
      setPreview(false);
      setConfirming(false);
      clearSelection();
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/email-center/send-test");
      toast.success(`Test email sent to ${data.sent_to}. Check your inbox.`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingTest(false); }
  };

  if (!isAdmin) {
    return (
      <SiteShell>
        <div className="container-page py-16 text-center text-muted-foreground">Admins only.</div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <section className="container-page py-14" data-testid="admin-email-center">
        <div className="flex items-center gap-3">
          <span className="chip inline-flex items-center gap-1.5"><Mail size={14} /> Admin</span>
          <span className="chip inline-flex items-center gap-1.5 bg-[hsl(var(--sb-forest))]/10 text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest))]/30" data-testid="admin-email-mode-chip">Live · Resend</span>
        </div>
        <h1 className="mt-4 font-serif text-4xl tracking-tight">Email Center</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Compose broadcast updates for beta users. Emails dispatch through Resend to every opted-in recipient. Opt-outs, pending-consent accounts, and demo profiles are automatically excluded.
        </p>
        <div className="mt-4">
          <button
            className="btn-ghost border inline-flex items-center gap-2 text-sm"
            onClick={sendTest}
            disabled={sendingTest}
            data-testid="admin-email-send-test-btn"
          >
            <TestTube2 size={14} />
            {sendingTest ? "Sending test…" : "Send test to my inbox"}
          </button>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatTile label="Total registered users" value={stats.total} testid="admin-email-stat-total" />
            <StatTile label="Opted in" value={stats.opted_in} tone="forest" testid="admin-email-stat-opted-in" />
            <StatTile label="Opted out" value={stats.opted_out} tone="muted" testid="admin-email-stat-opted-out" />
            <StatTile label="Pending consent" value={stats.pending_consent ?? 0} tone="clay" testid="admin-email-stat-pending-consent" />
          </div>
        )}

        {lastSent && (
          <div className="mt-6 rounded-xl border border-[hsl(var(--sb-forest))]/30 bg-[hsl(var(--sb-forest))]/5 p-4 flex items-start gap-3" data-testid="admin-email-send-confirmation">
            <CheckCircle2 size={18} className="text-[hsl(var(--sb-forest-2))] mt-0.5" />
            <div className="text-sm">
              <div className="font-medium">
                Delivered to {lastSent.delivered ?? lastSent.recipient_count} of {lastSent.recipient_count} recipient{lastSent.recipient_count === 1 ? "" : "s"}.
              </div>
              {(lastSent.failed ?? 0) > 0 && (
                <div className="text-[hsl(var(--sb-clay))] mt-1">{lastSent.failed} delivery failure{lastSent.failed === 1 ? "" : "s"} — check the audit trail.</div>
              )}
              {lastSent.skipped_opt_out > 0 && (
                <div className="text-muted-foreground mt-1">{lastSent.skipped_opt_out} opted-out user{lastSent.skipped_opt_out === 1 ? "" : "s"} did not receive the email.</div>
              )}
            </div>
          </div>
        )}

        <div className="mt-8"><ConfidentialityCatchupCard /></div>

        <div className="mt-8 grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
          <div className="card-soft space-y-4">
            <h2 className="font-serif text-2xl">Compose</h2>
            <input
              className="sb-input"
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="admin-email-subject"
            />
            <textarea
              className="sb-input min-h-[220px] resize-y"
              placeholder="Write your update…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="admin-email-body"
            />
            <div className="flex flex-wrap gap-2 justify-end">
              <button className="btn-ghost border" onClick={() => setPreview(true)} disabled={!subject.trim() || !body.trim()} data-testid="admin-email-preview-btn">
                Preview
              </button>
              <button className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" onClick={openConfirm} disabled={!canSend || blockedBroadcast} data-testid="admin-email-send-btn">
                <Send size={14} />
                {sending
                  ? "Sending…"
                  : isTargeted
                    ? `Send to ${targetedCount} selected`
                    : blockedBroadcast
                      ? "Select recipients to send"
                      : `Send to all ${stats ? broadcastCount : "…"} opted-in`}
              </button>
            </div>
            {outsideFilter > 0 && (
              <OutsideFilterWarning count={outsideFilter} label={audienceText(roles, appliedActivity)} onRemove={removeOutside} testid="admin-email-outside-warning-compose" />
            )}
            {blockedBroadcast && (
              <p className="text-xs text-[hsl(var(--sb-clay))]" data-testid="admin-email-select-hint">
                {!stats
                  ? "Recipient stats are unavailable right now. Select recipients to send, or reload the page."
                  : isFiltered
                    ? "Filters are on. Select recipients (or use \"Select all matching\") before sending, or switch back to All Members to broadcast."
                    : "There are no opted-in recipients to send to."}
              </p>
            )}
            <p className="text-xs text-muted-foreground" data-testid="admin-email-send-summary">
              <AlertCircle size={11} className="inline mr-1" />
              {isTargeted
                ? `Targeted send: ${targetedCount} recipient${targetedCount === 1 ? "" : "s"} selected. `
                : blockedBroadcast
                  ? "No recipients selected. "
                  : "Broadcast: all opted-in users will receive this. "}
              Emails are sent via Resend and logged to the audit trail.
            </p>
          </div>

          <div className="card-soft space-y-4">
            <EmailRecipientFilters
              roles={roles}
              activity={activity}
              onRoles={(v) => { setRoles(v); setShowSelected(false); }}
              onActivity={(v) => { setActivity(v); setShowSelected(false); }}
              includeQa={includeQa}
              onIncludeQa={toggleIncludeQa}
              qaCount={stats?.qa_opted_in ?? 0}
              demoCount={stats?.demo_opted_in_excluded ?? 0}
            />
            <div className="flex items-center gap-2">
              <Search size={16} className="text-muted-foreground" />
              <input
                className="sb-input flex-1"
                placeholder="Search recipients…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="admin-email-recipient-search"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-xs font-medium cursor-pointer" data-testid="admin-email-recipient-select-all-wrapper">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border accent-[hsl(var(--sb-forest))] cursor-pointer"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  disabled={recipients.length === 0}
                  data-testid="admin-email-recipient-select-all"
                />
                {allVisibleSelected ? `Deselect all ${recipients.length} matching` : `Select all ${recipients.length} matching`}
              </label>
              {selectedIds.size > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-xs text-muted-foreground underline hover:text-foreground"
                  data-testid="admin-email-recipient-clear-selection"
                >
                  Clear selection ({selectedIds.size})
                </button>
              )}
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <div data-testid="admin-email-match-count">
                <span className="font-semibold text-foreground">{recipients.length}</span> {stats ? `of ${broadcastCount} ` : ""}opted-in recipients match{isFiltered ? ` (${filterLabel})` : ""}
              </div>
              <div className="flex flex-wrap items-center gap-x-2" data-testid="admin-email-selected-count">
                <span className="font-medium text-[hsl(var(--sb-forest-2))]">{selectedIds.size} selected</span>
                {outsideFilter > 0 && <span>({outsideFilter} outside current filters)</span>}
                {selectedIds.size > 0 && (
                  <button type="button" className="underline hover:text-foreground" onClick={() => setShowSelected((v) => !v)} data-testid="admin-email-show-selected">
                    {showSelected ? "Show filtered list" : "Show selected only"}
                  </button>
                )}
              </div>
            </div>
            {outsideFilter > 0 && (
              <OutsideFilterWarning count={outsideFilter} label={audienceText(roles, appliedActivity)} onRemove={removeOutside} onReview={() => setShowSelected(true)} testid="admin-email-outside-warning" />
            )}
            <ul className="max-h-[360px] overflow-y-auto divide-y divide-border/60" data-testid="admin-email-recipient-list">
              {listed.length === 0 && <li className="py-6 text-center text-sm text-muted-foreground">No opted-in recipients match these filters.</li>}
              {listed.map((r) => {
                const checked = selectedIds.has(r.id);
                return (
                  <li key={r.id} className="py-2">
                    <label
                      className="flex items-center gap-3 rounded-lg px-1 py-1 cursor-pointer hover:bg-muted/40 transition-colors"
                      data-testid={`admin-email-recipient-row-${r.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(r)}
                        className="h-4 w-4 rounded border-border accent-[hsl(var(--sb-forest))] cursor-pointer"
                        data-testid={`admin-email-recipient-checkbox-${r.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{r.display_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                        <RecipientBadges r={r} />
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="mt-10 card-soft">
          <div className="flex items-center gap-2 mb-4">
            <History size={16} className="text-muted-foreground" />
            <h2 className="font-serif text-2xl">Send history</h2>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sends yet.</p>
          ) : (
            <ul className="divide-y divide-border/60" data-testid="admin-email-history">
              {history.map((h) => (
                <li key={h.id}>
                  <button
                    type="button"
                    onClick={() => openDetail(h.id)}
                    data-testid={`admin-email-history-row-${h.id}`}
                    className="w-full py-3 flex items-center justify-between gap-4 text-left hover:bg-muted/40 rounded-lg px-2 -mx-2 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{h.subject}</span>
                        <SendTypeChip sendType={h.send_type} />
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{new Date(h.sent_at).toLocaleString()} · {h.delivery_mode}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-[hsl(var(--sb-forest-2))]">
                        {(h.delivered ?? h.recipient_count)} sent
                      </div>
                      {(h.failed ?? 0) > 0 && <div className="text-[11px] text-[hsl(var(--sb-clay))]">{h.failed} failed</div>}
                      {h.skipped_opt_out > 0 && <div className="text-[11px] text-muted-foreground">{h.skipped_opt_out} skipped</div>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-10 card-soft" data-testid="admin-consent-history-section">
          <div className="flex items-center gap-2 mb-1">
            <History size={16} className="text-muted-foreground" />
            <h2 className="font-serif text-2xl">Consent history</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Every recorded opt-in / opt-out change with its source. One-click
            unsubscribes from broadcast emails show up as <span className="font-medium">email_link</span>.
          </p>
          {consentEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No consent changes recorded yet.</p>
          ) : (
            <ul className="divide-y divide-border/60" data-testid="admin-consent-events">
              {consentEvents.map((e) => (
                <li key={e.id} className="py-2.5 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{e.display_name || e.email}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {e.email} · {new Date(e.recorded_at).toLocaleString()} · <span className="uppercase tracking-wide text-[10px]">{e.source}</span>
                    </div>
                  </div>
                  <span
                    className={
                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium shrink-0 " +
                      (e.opt_in
                        ? "bg-[hsl(var(--sb-forest))]/10 text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest))]/30"
                        : "bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay))] border-[hsl(var(--sb-clay))]/30")
                    }
                    data-testid={`admin-consent-status-${e.id}`}
                  >
                    {e.opt_in ? "Opted in" : "Opted out"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {preview && (
          <div role="dialog" className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="admin-email-preview-modal">
            <div className="absolute inset-0 bg-foreground/50" onClick={() => setPreview(false)} />
            <div className="relative max-w-lg w-full rounded-2xl border border-border bg-background shadow-xl p-6">
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Email preview</div>
              <div className="mt-3 pb-3 border-b">
                <div className="text-[11px] text-muted-foreground">Subject</div>
                <div className="font-serif text-xl mt-0.5">{subject}</div>
              </div>
              <div className="mt-4 text-sm whitespace-pre-wrap leading-relaxed">{body}</div>
              <div className="mt-6 flex justify-end gap-2">
                <button className="btn-ghost border" onClick={() => setPreview(false)}>Close</button>
                <button className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" onClick={openConfirm} disabled={!canSend || blockedBroadcast} data-testid="admin-email-preview-send">
                  <Send size={14} /> Send to {outboundCount} {isTargeted ? "selected" : "users"}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirming && (
          <SendConfirmDialog
            audience={audienceLabel}
            count={outboundCount}
            subject={subject}
            outsideCount={outsideFilter}
            outsideLabel={audienceText(roles, appliedActivity)}
            onRemoveOutside={removeOutside}
            sending={sending}
            onCancel={() => setConfirming(false)}
            onConfirm={send}
          />
        )}

        {detail && (
          <SendDetailModal
            detail={detail}
            loading={detailLoading}
            onClose={() => setDetail(null)}
          />
        )}
      </section>
    </SiteShell>
  );
}

function SendDetailModal({ detail, loading, onClose }) {
  const meta = SEND_TYPE_META[detail.send_type] || SEND_TYPE_META.broadcast;
  const sentAtDate = detail.sent_at ? new Date(detail.sent_at) : null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-3 sm:p-6"
      data-testid="admin-email-detail-modal"
    >
      <div className="absolute inset-0 bg-foreground/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-border bg-background shadow-xl">
        <div className="sticky top-0 z-10 bg-background border-b border-border/70 flex items-start justify-between gap-3 px-5 sm:px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Send details</span>
              <SendTypeChip sendType={detail.send_type} />
            </div>
            <h3 className="mt-1 font-serif text-2xl leading-tight truncate" data-testid="admin-email-detail-subject">
              {detail.subject || "(no subject)"}
            </h3>
            <div className="mt-1 text-xs text-muted-foreground">
              {sentAtDate ? sentAtDate.toLocaleString() : "—"}
              {detail.sent_by?.display_name && <> · from {detail.sent_by.display_name}</>}
              {detail.audience_label && <> · <span data-testid="admin-email-detail-audience">{detail.audience_label}</span></>}
              {" · "}
              <span className="uppercase tracking-wide text-[10px]">{detail.delivery_mode}</span>
            </div>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:bg-muted"
            onClick={onClose}
            aria-label="Close"
            data-testid="admin-email-detail-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryTile label="Recipients" value={detail.recipient_count} />
            <SummaryTile label="Delivered" value={detail.delivered} tone="forest" />
            <SummaryTile label="Failed" value={detail.failed} tone={detail.failed ? "clay" : undefined} />
            <SummaryTile label="Skipped" value={detail.skipped_opt_out} tone="muted" />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">Full message</div>
            <div
              className="rounded-xl border border-border bg-muted/40 p-4 text-sm leading-relaxed whitespace-pre-wrap"
              data-testid="admin-email-detail-body"
            >
              {detail.body || "(no body)"}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                Recipients ({detail.recipients?.length || 0})
              </div>
            </div>
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : (detail.recipients?.length || 0) === 0 ? (
              <div className="text-sm text-muted-foreground">No recipients recorded.</div>
            ) : (
              <ul className="divide-y divide-border/60 rounded-xl border border-border/70" data-testid="admin-email-detail-recipients">
                {detail.recipients.map((r, idx) => (
                  <li key={(r.id || r.email || "row") + idx} className="px-3 py-2.5 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{r.display_name || r.email || "—"}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.email || "—"}</div>
                      {r.error && (
                        <div className="text-[11px] text-[hsl(var(--sb-clay))] mt-1 break-all">{r.error}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span
                        className={
                          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border " +
                          (r.status === "delivered"
                            ? "bg-[hsl(var(--sb-forest))]/10 text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest))]/30"
                            : "bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay))] border-[hsl(var(--sb-clay))]/30")
                        }
                      >
                        {r.status === "delivered" ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
                        {r.status || "delivered"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-border/70 bg-background px-5 sm:px-6 py-3 flex justify-end">
          <button className="btn-ghost border" onClick={onClose} data-testid="admin-email-detail-close-btn">Close</button>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  const toneClass = tone === "forest"
    ? "bg-[hsl(var(--sb-forest))]/10 border-[hsl(var(--sb-forest))]/30 text-[hsl(var(--sb-forest-2))]"
    : tone === "clay"
      ? "bg-[hsl(var(--sb-clay))]/10 border-[hsl(var(--sb-clay))]/30 text-[hsl(var(--sb-clay))]"
      : tone === "muted"
        ? "bg-muted border-border text-muted-foreground"
        : "bg-background border-border";
  return (
    <div className={`rounded-xl border p-3 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] opacity-75">{label}</div>
      <div className="mt-0.5 font-serif text-xl">{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

function StatTile({ label, value, tone, testid }) {
  const toneClass = tone === "forest"
    ? "bg-[hsl(var(--sb-forest))]/8 border-[hsl(var(--sb-forest))]/25 text-[hsl(var(--sb-forest-2))] dark:text-emerald-200 dark:border-emerald-300/40"
    : tone === "muted"
      ? "bg-muted border-border text-muted-foreground"
      : tone === "clay"
        ? "bg-[hsl(var(--sb-clay))]/8 border-[hsl(var(--sb-clay))]/25 text-[hsl(var(--sb-clay))] dark:text-orange-200 dark:border-orange-300/40"
        : "bg-background border-border";
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`} data-testid={testid || "admin-email-stat-tile"}>
      <div className="text-[11px] uppercase tracking-[0.14em] opacity-75">{label}</div>
      <div className="mt-1 font-serif text-3xl">{value.toLocaleString()}</div>
    </div>
  );
}
